import React, { useCallback } from 'react';
import { Submission } from 'redux/schemas/models/submissions';

import { AngularContext } from 'react-app';
import _ from 'underscore';
import t from 'react-translate';

import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import NvSubmissionCard from 'shared/components/submission/nv-submission-card';
import NvSubmissionGallery from 'shared/components/submission/nv-submission-gallery';
import NvSubmissionPlaceholderCard from 'shared/components/submission/nv-submission-placeholder-card';

import { gray6 } from 'styles/global_defaults/colors';
import { standardSpacing } from 'styles/global_defaults/scaffolding';

const getcardClassname = (isPrimary) => {
  if (isPrimary) {
    return 'd-flex align-items-center justify-content-center flex-column text-small text-primary';
  }
  return 'd-flex align-items-center justify-content-center flex-column gray-2 text-small text-gray-2';
};

type PlaceholderCard = { isPlaceholder: boolean, lowSubmissionCount?: boolean, submissionApprovalDashboard?: boolean };
interface SubmissionsCarouselProps {
  submissions: Submission[] | PlaceholderCard[]
  showApprovalStatus?: boolean
  isPublicFeedbackOn?: boolean
  showApprovalPendingTime?: boolean
  showDueDate?: boolean
  showRevisionRemainderTime?: boolean
  hideSocial?: boolean
  showCourseHeaderInLink?: boolean
}

const SubmissionsCarousel: React.FC<SubmissionsCarouselProps> = ({
  submissions,
  showApprovalStatus,
  isPublicFeedbackOn,
  showApprovalPendingTime,
  showDueDate,
  showRevisionRemainderTime,
  hideSocial,
  showCourseHeaderInLink,
}) => {
  const { injectServices } = React.useContext(AngularContext);
  const [$stateParams, ReportsManager, $state, CurrentCourseManager] = injectServices(['$stateParams', 'ReportsManager', '$state', 'CurrentCourseManager']);

  const filterOption = isPublicFeedbackOn ? 'not_my_comments' : null;

  const setReports = useCallback((submission) => {
    let submissionCatalogId;
    if (submission.exercise) {
      submissionCatalogId = submission.exercise.catalogId || submission.exercise.course?.catalogId;
    }

    if (!submissionCatalogId) {
      submissionCatalogId = $stateParams.catalogId;
    }

    const reports = [...submissions as any];
    if (_.last(reports).isPlaceholder) {
      reports.pop();
    }

    ReportsManager.initialize({
      reportsCatalogId: $stateParams.catalogId,
      reports,
    });

    $state.go('individual-submission-modal', {
      catalogId: submissionCatalogId,
      reportId: submission.id,
      galleryMode: true,
      showCourseHeader: !!showCourseHeaderInLink,
    });
  }, [$state, $stateParams.catalogId, ReportsManager, showCourseHeaderInLink, submissions]);

  const handleSeeMore = useCallback((submissionApprovalDashboard) => {
    if (submissionApprovalDashboard) {
      $state.go('submissions-gallery');
    } else {
      $state.go('submissions-gallery', {
        catalogId: CurrentCourseManager.course.catalogId,
        exerciseId: (submissions[0] as Submission)?.exerciseId,
        filterOption,
      });
    }
  }, [$state, CurrentCourseManager.course.catalogId, filterOption, submissions]);

  const getPlaceHolderText = useCallback((submission) => {
    if (submission.submissionApprovalDashboard) {
      return t.DASHBOARD.SHOW_ALL();
    }
    if (submission.lowSubmissionCount) {
      return isPublicFeedbackOn ? t.INFORMAL_FEEDBACK.NO_MORE_SUBMISSIONS() : t.SUBMISSIONS.NO_MORE_SUBMISSIONS();
    }
    return t.SUBMISSIONS.SEE_MORE_IN_SUBMISSIONS_GALLERY();
  }, [isPublicFeedbackOn]);

  const galleryCards = submissions?.map((submission, index) => {
    if ((submission as PlaceholderCard).isPlaceholder) {
      return (
        <NvSubmissionPlaceholderCard
          key='submission-placholder'
          style={{
            width: '260px',
            minWidth: '208px',
            maxWidth: '260px',
          }}
          content={(
            <ClickableContainer
              className={getcardClassname(!submission.lowSubmissionCount || submission.submissionApprovalDashboard)}
              onClick={() => handleSeeMore(submission.submissionApprovalDashboard)}
              layoutOnly={submission.lowSubmissionCount}
            >
              <NvIcon size='small' icon='gallery' className='mb-3' />
              <div className='d-flex text-center justify-content-center align-items-center'>
                {getPlaceHolderText(submission)}
              </div>
            </ClickableContainer>
        )}
        />
      );
    }
    return (
      <NvSubmissionCard
        key={`submission-${submission.id}`}
        submission={submission}
        style={{ width: '260px', minWidth: '208px', maxWidth: '260px' }}
        showStatus={isPublicFeedbackOn && submission.gavePublicFeedback}
        statusClass='bg-gray-2'
        statusContent={(<NvIcon icon='check' size='medium' />)}
        stateParams={{
          galleryMode: true,
          isRatingFeedback: false,
        }}
        showApprovalStatus={showApprovalStatus}
        onClickSubmission={() => setReports(submission)}
        showApprovalPendingTime={showApprovalPendingTime}
        showDueDate={showDueDate}
        showRevisionRemainderTime={showRevisionRemainderTime}
        hideSocial={hideSocial}
      />
    );
  });

  return (
    <div>
      <NvSubmissionGallery
        spacing={standardSpacing}
        backgroundColor={gray6}
        arrowsHidden={!submissions.length}
        topPadding={standardSpacing}
        bottomPadding={standardSpacing}
        carouselLabel={t.SUBMISSIONS.TITLE()}
        draggable
      >
        {galleryCards}
      </NvSubmissionGallery>
    </div>
  );
};

export default SubmissionsCarousel;
